import { useQuery } from '@tanstack/react-query';

import { queryOptions } from '~components/hooks/_constants';
import { ActiveCollateralAssets, checkValidContractAddress, getContractAddress } from '~config/protocol';
import { EVestaAsset } from '~config/types';
import { queryClient } from '~pages/_app';
import { getVestaParameters } from '~services/contracts/vestaParameters';
import { IParameters } from '~services/types';
import { ERROR_MSGS } from '~utils/error/errors';
import { BIG_INT_ZERO, setInitialState } from '~utils/utils';

import { useContractVestaParameters } from '../contracts';
import { useSession } from '../useSession';

import { AllParamsQueryResponse, DeterminedQueryResult } from './_types';

export const initialParamState: IParameters = {
  bonusToSp: 0,
  mcr: 1.1,
  minNetDebt: 300,
  minNetDebtRaw: BigInt(300 * 10 ** 18),
  vstMintCap: 0,
  vstMintCapRaw: BIG_INT_ZERO,
};

export const VestaParametersDataQueryKey = 'parameters';
export const AllVestaParametersDataQueryKey = 'all-parameters';

const useVestaParametersData = (asset: EVestaAsset): DeterminedQueryResult<IParameters> => {
  const {
    network: { networkId },
  } = useSession();
  const { read: vestaParametersRead, address: vestaParametersAddress } = useContractVestaParameters();
  const tokenAddress = getContractAddress(asset, networkId);

  const invalidate = () => queryClient.refetchQueries([VestaParametersDataQueryKey, networkId, asset]);

  const result = useQuery<IParameters>(
    [VestaParametersDataQueryKey, networkId, asset],
    async () => {
      return getVestaParameters(tokenAddress, vestaParametersRead);
    },
    {
      retry: (failureCount: number, error: any) => {
        if (error.message === ERROR_MSGS.REACT_QUERY_CANNOT_FETCH || failureCount > 5) return false;
        return true;
      },
      retryDelay: attempt => Math.min(attempt > 1 ? 2 ** attempt * 1000 : 1000, 30 * 1000),
      staleTime: Infinity,
      refetchOnMount: false,
      enabled: checkValidContractAddress([tokenAddress, vestaParametersAddress]),
    },
  );

  return {
    ...result,
    data: result.data ?? initialParamState,
    invalidate,
  } as DeterminedQueryResult<IParameters>;
};

const initalAllparams = setInitialState(EVestaAsset, initialParamState);

//@TODO change to new fetching pattern
export const useAllVestaParameters = () => {
  const {
    network: { networkId },
  } = useSession();
  const { read: vestaParametersRead, address: vestaParametersAddress } = useContractVestaParameters();
  const invalidate = () => queryClient.refetchQueries([AllVestaParametersDataQueryKey, networkId]);

  const result = useQuery<AllParamsQueryResponse | undefined>(
    [AllVestaParametersDataQueryKey, networkId],
    async () => {
      try {
        const paramsData = await Promise.all(
          ActiveCollateralAssets.map(async asset => {
            const tokenAddress = getContractAddress(asset, networkId);

            const params = await getVestaParameters(tokenAddress, vestaParametersRead);
            return { asset, params };
          }),
        );

        return paramsData.reduce<AllParamsQueryResponse>(
          (a, b) => ({
            ...a,
            [b.asset]: b.params,
          }),
          {} as AllParamsQueryResponse,
        );
      } catch (err) {
        console.error(err);
      }
    },
    queryOptions(false, checkValidContractAddress(vestaParametersAddress)),
  );

  return { ...result, data: result.data ?? (initalAllparams as AllParamsQueryResponse), invalidate };
};

export default useVestaParametersData;
